"use client";

import PropTypes from "prop-types";
import { Fragment, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import navigationLinks from "config/navigationLinks";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";

function isActive(link, path) {
  return path.replace(".html", "") === link.replace(".html", "");
}

function parseName(name) {
  const parts = name.split(/(<sup>.*?<\/sup>)/);
  return parts.map((part, index) => {
    if (part.startsWith("<sup>")) {
      const content = part.replace(/<\/?sup>/g, "");
      return <sup key={index}>{content}</sup>;
    }
    return part;
  });
}

function Chevron({ open }) {
  return (
    <FontAwesomeIcon
      icon={faChevronRight}
      className={`w-2.5 h-2.5 ml-2 mr-1 text-white lg:text-gray-500 transition ${
        open ? "rotate-90" : "rotate-0"
      }`}
    />
  );
}

function NavigationItem({
  children,
  name,
  icon,
  link,
  pages,
  activePath,
  className,
  classNameActive,
}) {
  const pathname = usePathname();
  const defaultActiveLink = pathname.startsWith(activePath);
  const [isActiveLink, setIsActiveLink] = useState(defaultActiveLink);

  useEffect(() => {
    setIsActiveLink(defaultActiveLink);
  }, [defaultActiveLink]);

  if (link?.includes("index.html")) {
    throw new Error(
      `Please don't use /category/example/index.html inside navigationLinks.js, use /category/example.html instead.`
    );
  }

  return (
    <Fragment key={name}>
      {pages ? (
        <li data-role="accordion">
          {/* item with chevron */}
          <span
            aria-hidden="true"
            data-role="accordion-head"
            className={className}
            onClick={() => setIsActiveLink(!isActiveLink)}
          >
            {icon && <FontAwesomeIcon icon={icon} className="w-3 h-3 mr-3" />}
            <span className="flex-grow c-beta leading-normal">
              {parseName(name)}
            </span>
            {pages && <Chevron open={isActiveLink} />}
          </span>
          {isActiveLink && <>{children}</>}
        </li>
      ) : (
        <li>
          {/* item with link */}
          <Link
            href={link}
            className={
              isActive(link, pathname)
                ? `leading-normal py-[5.5px] ${className} ${classNameActive}`
                : `leading-normal py-[5.5px] ${className}`
            }
          >
            {icon && <FontAwesomeIcon icon={icon} className="w-3 h-3 mr-3" />}
            <span className="c-beta">{parseName(name)}</span>
          </Link>
        </li>
      )}
    </Fragment>
  );
}

export default function Navigation() {
  const style = {
    item: "no-underline cursor-pointer lg:hover:text-blue hover:text-blue",
    active: "font-medium text-blue-200 lg:text-blue",
    border: "border-l border-gray-200 hover:border-blue-200",
  };

  return (
    <nav className="text-gray-100 lg:text-gray-800 text-base lg:text-sm">
      <ul className="space-y-6">
        {navigationLinks.map(({ name, sub_sections }) => (
          <li key={name}>
            <h2 className="c-heading--caps font-medium text-white lg:text-gray-600">
              {name}
            </h2>

            {sub_sections && (
              <ul>
                {sub_sections.map(({ ...subSectionProps }) => (
                  <NavigationItem
                    key={subSectionProps.name}
                    {...subSectionProps}
                    className={`flex items-center py-2 lg:py-1 pl-1 ${style.item}`}
                    classNameActive={`${style.active}`}
                  >
                    {/* Pages */}
                    {subSectionProps?.pages && (
                      <ul className="py-2 ml-2 leading-8">
                        {subSectionProps.pages?.map(({ ...pagesProps }) => (
                          <NavigationItem
                            key={pagesProps.name}
                            {...pagesProps}
                            className={`block pl-5 ${style.item} ${style.border}`}
                            classNameActive={`${style.active}`}
                          >
                            {/* Sub Pages */}
                            {pagesProps?.pages && (
                              <ul className="py-2 pl-5 border-l border-gray-200">
                                {pagesProps.pages?.map(
                                  ({ ...subPagesProps }) => (
                                    <NavigationItem
                                      key={subPagesProps.name}
                                      {...subPagesProps}
                                      className={`block pl-4 ${style.item} ${style.border}`}
                                      classNameActive={`${style.active}`}
                                    >
                                      {/* Sub Sub Pages */}
                                      <ul className="py-2 pl-4 border-l border-gray-200">
                                        {subPagesProps.pages?.map(
                                          ({ ...subSubPagesProps }) => (
                                            <NavigationItem
                                              key={subSubPagesProps.name}
                                              {...subSubPagesProps}
                                              className={`block pl-4 border-l ${style.item} ${style.border}`}
                                              classNameActive={`${style.active}`}
                                            ></NavigationItem>
                                          )
                                        )}
                                      </ul>
                                    </NavigationItem>
                                  )
                                )}
                              </ul>
                            )}
                          </NavigationItem>
                        ))}
                      </ul>
                    )}
                  </NavigationItem>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

Chevron.propTypes = {
  open: PropTypes.bool,
};

NavigationItem.propTypes = {
  activePath: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  classNameActive: PropTypes.string,
  icon: PropTypes.object,
  link: PropTypes.string,
  name: PropTypes.string,
  pages: PropTypes.array,
};
